<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import IssueClusterMap from './IssueClusterMap.vue';

const props = defineProps({
  clusterId: { type: [String, Number], default: null },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const selectedSources = ref([]);
const selectedTickets = ref([]);

const cluster = computed(() =>
  store.getters['issueClusters/getIssueCluster'](Number(props.clusterId))
);
const uiFlags = computed(() => store.getters['issueClusters/getUIFlags']);
const members = computed(() => cluster.value?.tickets || []);

const mergeableClusters = computed(() =>
  store.getters['issueClusters/getIssueClusters'].filter(
    candidate =>
      candidate.id !== Number(props.clusterId) &&
      ['open', 'monitoring', 'dispatched'].includes(candidate.status)
  )
);

const mapPoints = computed(() => {
  if (!cluster.value) return [];
  const points = [];
  if (cluster.value.centroid_lat && cluster.value.centroid_lng) {
    points.push({
      lat: cluster.value.centroid_lat,
      lng: cluster.value.centroid_lng,
      label: t('ISSUE_CLUSTERS.DETAIL.CENTROID'),
    });
  }
  members.value.forEach(ticket => {
    if (ticket.latitude && ticket.longitude) {
      points.push({ lat: ticket.latitude, lng: ticket.longitude, label: ticket.title });
    }
  });
  return points;
});

const goBack = () => router.push(accountScopedRoute('issue_clusters_index'));

const reload = () => store.dispatch('issueClusters/show', props.clusterId);

const onMerge = async () => {
  if (!selectedSources.value.length) return;
  await store.dispatch('issueClusters/merge', {
    id: props.clusterId,
    sourceIds: selectedSources.value,
  });
  selectedSources.value = [];
  await reload();
};

const onSplit = async () => {
  if (!selectedTickets.value.length) return;
  await store.dispatch('issueClusters/split', {
    id: props.clusterId,
    ticketIds: selectedTickets.value,
  });
  selectedTickets.value = [];
  await reload();
};

onMounted(() => {
  store.dispatch('issueClusters/show', props.clusterId);
  store.dispatch('issueClusters/fetch', {});
});
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 overflow-auto">
    <Button
      variant="link"
      color="slate"
      size="sm"
      icon="i-lucide-arrow-left"
      :label="t('ISSUE_CLUSTERS.DETAIL.BACK')"
      class="self-start mb-4"
      @click="goBack"
    />

    <div v-if="cluster" class="flex flex-col gap-6">
      <div class="flex items-center gap-4">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('ISSUE_CLUSTERS.DETAIL.TITLE', { id: cluster.display_id }) }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t(`ISSUE_CLUSTERS.STATUS.${(cluster.status || '').toUpperCase()}`) }}
          · {{ t('ISSUE_CLUSTERS.LIST.REPORTS', { count: cluster.reports_count }) }}
        </span>
      </div>

      <IssueClusterMap v-if="mapPoints.length" :points="mapPoints" />
      <p v-else class="text-sm text-n-slate-11">
        {{ t('ISSUE_CLUSTERS.DETAIL.NO_LOCATION') }}
      </p>

      <section>
        <h2 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('ISSUE_CLUSTERS.DETAIL.MEMBERS') }}
        </h2>
        <ul class="flex flex-col gap-1">
          <li
            v-for="ticket in members"
            :key="ticket.id"
            class="flex items-center gap-2 text-sm text-n-slate-11"
          >
            <input v-model="selectedTickets" type="checkbox" :value="ticket.id" />
            <span class="font-medium text-n-slate-12">#{{ ticket.display_id }}</span>
            <span>{{ ticket.title }}</span>
          </li>
        </ul>
        <Button
          v-if="members.length > 1"
          class="mt-2"
          variant="outline"
          color="amber"
          size="sm"
          :label="t('ISSUE_CLUSTERS.ACTIONS.SPLIT')"
          :is-loading="uiFlags.isSplitting"
          :disabled="!selectedTickets.length"
          @click="onSplit"
        />
      </section>

      <section v-if="mergeableClusters.length">
        <h2 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('ISSUE_CLUSTERS.ACTIONS.MERGE') }}
        </h2>
        <select v-model="selectedSources" multiple class="w-full max-w-md text-sm rounded-md border border-n-weak bg-n-alpha-black1 text-n-slate-12 p-2">
          <option v-for="candidate in mergeableClusters" :key="candidate.id" :value="candidate.id">
            #{{ candidate.display_id }} — {{ t('ISSUE_CLUSTERS.LIST.REPORTS', { count: candidate.reports_count }) }}
          </option>
        </select>
        <Button
          class="mt-2"
          variant="solid"
          color="slate"
          size="sm"
          :label="t('ISSUE_CLUSTERS.ACTIONS.CONFIRM_MERGE')"
          :is-loading="uiFlags.isMerging"
          :disabled="!selectedSources.length"
          @click="onMerge"
        />
      </section>
    </div>
  </div>
</template>
